import { useCallback, useRef, useState } from 'react'
import { useSelector } from '@xstate/react'
import { SubmitProps as LocationPickerValue } from 'components/NavRightClickMenu/components/ActionModal/ActionModal'
import { CopyLocationPicker } from 'components/CopyModal/components/CopyLocationPicker/CopyLocationPicker'
import { AlmanacFile, ArticleFlat, Permission } from 'types'
import { ExternalLinkReviewAsset, ReviewAssetType } from '../types'
import { useHistory } from 'react-router-dom'
import { Button } from 'components/Button/Button'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { createNewArticle, getArticleLink } from 'utils'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import { reportError } from 'utils/error'
import { useReviewsWizard } from '../ReviewsWizardProvider'
import { EventId } from '../machineTypes'
import { TextInputField } from '../../Form/TextInputField'
import Styles from './FileSelection.module.scss'
import cx from 'classnames'
import { useImportModal } from 'hooks/useImportModal'
import { useNewEntrypointModal } from 'hooks/useNewEntrypointModal'
import useReviewCreationModal from 'hooks/useReviewCreationModal'
import { useRecentDocs } from 'hooks/useRecentDocs'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { RecentDocs } from './RecentDocs'
import { ExternalResourceRenderer } from 'screens/ProseEdit/components/ExternalResourceRenderer/ExternalResourceRenderer'
import { useValidateFileUrl } from './useValidateFileUrl'
import { LOADING_TITLE, FileType } from './urlValidationUtils'
import { EmptyPreview } from './EmptyPreview'
import { InstructionalScreenshot } from './InstructionalScreenshot'
import { AssetInput } from './AssetInput'
import { ReactComponent as ImportIcon } from 'assets/icons/import16.svg'
import { Modal } from 'almanac-ui/Modal'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useCurrentFolderStore } from 'hooks/stores/useCurrentFolderStore'

interface FileSelectionProps {
  overrideFolder?: AlmanacFile | null
  onClose: () => void
}

export const FileSelection = ({ overrideFolder, onClose }: FileSelectionProps) => {
  const { service } = useReviewsWizard()
  const history = useHistory()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const asset = useSelector(service, (state) => state.context.asset)
  const assetType = useSelector(service, (state) => state.context.assetType)
  const inputRef = useRef<HTMLInputElement>(null)
  const workspace = useCurrentWorkspace()
  const { openImportModal } = useImportModal()
  const { openNewEntrypointModal } = useNewEntrypointModal()
  const { openReviewModal } = useReviewCreationModal()
  const workspaceId = useCurrentWorkspaceId()
  const [chosenWorkspaceId, setChosenWorkspaceId] = useState(workspaceId)
  const { currentFolder } = useCurrentFolderStore()
  const [chosenFolder, setChosenFolder] = useState(overrideFolder ?? currentFolder)

  const { recentDocs, loading } = useRecentDocs({
    withActiveReviews: false,
    permissions: [Permission.Edit, Permission.Owner],
    skipProjects: true,
  })

  const { validateUrl, isValid, isEmbeddable, frontIcon, fileType, isLoading } = useValidateFileUrl({
    onTitleChange: (title: string) => {
      service.send(EventId.SET_ASSET_TITLE, {
        assetTitle: title,
      })
    },
    onIconChange: (icon: FileType) => {
      service.send(EventId.SET_EXTERNAL_FILE_ICON, { externalFileIcon: icon })
    },
  })
  const externalLink = assetType === ReviewAssetType.ExternalLink ? (asset as ExternalLinkReviewAsset).link || '' : ''

  const moveFolder = (newLocation: LocationPickerValue) => {
    setChosenWorkspaceId(newLocation.newWorkspaceId)
    setChosenFolder(newLocation.selectedFolder as AlmanacFile)
  }

  const handleNext = useCallback(() => {
    if (!asset) return

    const createExternalLinkArticle = async () => {
      setIsSubmitting(true)
      const workspaceId = chosenFolder?.workspace_id || workspace?.key
      if (!workspaceId) return

      const res = await createNewArticle({
        workspaceKey: workspaceId,
        projectId: chosenFolder?.project_id,
        title: asset.title,
        externalLink,
        externalFileIcon: (asset as ExternalLinkReviewAsset).icon,
      })

      setIsSubmitting(false)

      if (res.type === SUCCESS_RESPONSE_TYPE) {
        onClose()
        history.push(`${getArticleLink(res.data.article)}/reviews/new`)
      } else {
        reportError(new Error('Error while creating a new document'), { metaData: { res } })
        showErrorMessageToast('Something went wrong 😢')
      }
    }

    const inputsInvalid = !isEmbeddable || !isValid || asset.title.length < 1 || isLoading

    if (assetType === ReviewAssetType.ExternalLink) {
      if (inputsInvalid) return

      createExternalLinkArticle()
      return
    }

    service.send(EventId.CONTINUE)
  }, [
    asset,
    service,
    assetType,
    isEmbeddable,
    isValid,
    isLoading,
    chosenFolder,
    history,
    externalLink,
    workspace?.key,
    onClose,
  ])

  const handleExternalLinkChange = useCallback(
    (event: any) => {
      const validUrl = validateUrl(event.target.value)
      service.send(EventId.SET_EXTERNAL_LINK, { externalLink: validUrl || event.target.value })
    },
    [service, validateUrl],
  )

  const deselectDoc = () => {
    service.send(EventId.CLEAR)
  }

  const handleAssetTitleChange = useCallback(
    (event: any) => {
      service.send(EventId.SET_ASSET_TITLE, { assetTitle: event.target.value })
    },
    [service],
  )

  const externalLinkValid = externalLink && externalLink.length > 0 && isValid
  const showScreenshot = externalLinkValid && !isEmbeddable
  const showAssetTitleError = assetType === ReviewAssetType.ExternalLink && asset && asset.title?.length < 1

  const onDocSelected = (doc: ArticleFlat) => {
    service.send(EventId.SET_ASSET, { asset: doc, assetType: ReviewAssetType.Article })
  }

  return (
    <>
      <Modal.Top>
        <CopyLocationPicker
          disableRoot
          destination={{
            // TODO: Fix this the next time the file is edited.
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            newWorkspaceId: chosenWorkspaceId!,
            selectedFolder: chosenFolder || null,
          }}
          onDestinationChange={moveFolder}
          alternateButton
        />
      </Modal.Top>
      <Modal.Header title={externalLinkValid ? 'Configure your Review' : 'Select a file'} />
      <Modal.Content>
        <div className={Styles.contentContainer}>
          <div className={Styles.inputs}>
            <AssetInput
              selectedDoc={assetType === ReviewAssetType.ExternalLink ? null : (asset as ArticleFlat)}
              handleExternalLinkChange={handleExternalLinkChange}
              externalLink={externalLink}
              isEmbeddable={isEmbeddable}
              externalLinkValid={externalLinkValid}
              onDeselectDoc={deselectDoc}
              onDocSelected={onDocSelected}
            />
            {externalLinkValid && (
              <TextInputField
                autoFocus
                labelClassName={Styles.inputLabel}
                errorMessage={showAssetTitleError && 'You must enter a name for the file'}
                label="File Name"
                aria-label="File Name"
                onChange={handleAssetTitleChange}
                value={asset?.title}
                wrapperClassName={cx({ [Styles.disabled]: asset?.title === LOADING_TITLE })}
                className={cx(Styles.assetTitleInput, { [Styles.assetTitleInputWithError]: showAssetTitleError })}
                size="lg"
                isDisabled={asset?.title === LOADING_TITLE}
                frontIcon={frontIcon}
                ref={inputRef}
              />
            )}
            {showScreenshot && !!fileType && <InstructionalScreenshot fileType={fileType} />}
          </div>

          {externalLinkValid && !!fileType && (
            <div className={cx(Styles.filePreviewContainer, { [Styles.isEmpty]: !isEmbeddable })}>
              <label className={Styles.inputLabel}>Preview</label>
              {isEmbeddable ? (
                <ExternalResourceRenderer
                  externalLink={externalLink}
                  fileType={fileType}
                  customClass={Styles.filePreview}
                  icon={frontIcon}
                  title={asset?.title}
                  isPreview
                  showLoadingState
                />
              ) : (
                <EmptyPreview fileType={fileType} />
              )}
            </div>
          )}
        </div>

        {(recentDocs.length > 0 || loading) && !externalLinkValid && (
          <RecentDocs
            recentDocs={recentDocs}
            loading={loading}
            selectedDoc={assetType === ReviewAssetType.ExternalLink ? null : (asset as ArticleFlat)}
            onDocSelected={onDocSelected}
          />
        )}
        {!externalLinkValid && (
          <Button
            className={Styles.uploadButton}
            onPress={() =>
              openImportModal({
                onGoBack: () =>
                  openReviewModal({
                    overrideFolder: chosenFolder,
                    closeModal: () => {},
                    onGoBack: () => openNewEntrypointModal({ createInFolder: chosenFolder }),
                  }),
                startReview: true,
                overrideFolder: chosenFolder,
              })
            }
            size="lg"
            frontIcon={ImportIcon}
            variant="secondary"
          >
            Upload or Import
          </Button>
        )}
      </Modal.Content>
      <Modal.Footer>
        <Modal.FooterContent slot="right">
          <Button onPress={handleNext} size="xl" isDisabled={isSubmitting}>
            Start Your Review
          </Button>
        </Modal.FooterContent>
      </Modal.Footer>
    </>
  )
}
